import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import {
  CollaborationTemplateDto,
  RequestDto,
} from '@oncolens/oncolens-ui/lib/dto/event/request';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useRequestFormStore from './store';
import { saveAndSubmitRequest, useUpdateSaveAndSubmitRequest } from '@/features/requests/api/saveAndSubmitRequest';
import { useWorkflow, WorkflowDto } from '@/features/requests/api/getWorkflow';
import { useQueryClient } from 'react-query';
import { biomarkerKeys } from './Biomarkers/biomarkers.keys';

interface NewRequestSubmissionProps {
  template: CollaborationTemplateDto | undefined;
  showAutosave?: boolean;
  testSelectorPrefix?: string;
  includeSectionTitle?: boolean;
}

const NewRequestSubmission = ({
  template,
  showAutosave = true,
  testSelectorPrefix = 'new-request-submission',
  includeSectionTitle = false,
}: NewRequestSubmissionProps) => {
  const { isValid, getValues, isDirty, markClean } =
    useRequestFormStore();

  const { data: workflow } = useWorkflow({ id: template?.workflowId ?? '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formValue = getValues();
  const navigate = useNavigate();
  const updateRequest = useUpdateSaveAndSubmitRequest();
  const queryClient = useQueryClient();

  const onSave = React.useCallback(
    (isSubmit?: boolean) => {
      const promises = [];
      const values = getValues();

      if (isDirty('submitTo') || isSubmit) {
        if (values.id) {
          promises.push(updateRequest.mutateAsync({
            eventId: values.id,
            data: values,
          }, {
            onSuccess: () => {
              queryClient.removeQueries(biomarkerKeys.panels(values.patient.id));
            }
          }));
        }
        else {
          promises.push(createNewRequest(workflow, getValues)
            .then((res) => res && onCreateSuccess(res)));
        }
      }

      return Promise.all(promises).then(() => {
        markClean();
      });
    },
    [getValues, isDirty, markClean, updateRequest]
  );

  const onClickSave = () => {

    setIsSubmitting(true);
    onSave(true)
      .then(() => {
        formValue.id && navigate(`/requests/splash?requestId=${formValue.id}`);
      })
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  const onCreateSuccess = (request: RequestDto) => {
    navigate(`/requests/splash?requestId=${request.id}`);
  };

  return (
    <>
      <div className="flex items-center mb-6">
        {includeSectionTitle && (
          <>
            <h2 className="text-2xl font-bold tracking-tight text-gray-800">
              4. Submit your request
            </h2>
            <span className="flex-1 block h-px mx-2 bg-gray-300"></span>
          </>
        )}
        <div className="w-full mt-14 sm:w-auto">
          <ButtonWithLoading
            className="justify-center w-full px-3 py-1.5 order-2 sm:order-none"
            loading={isSubmitting}
            disabled={!isValid}
            buttonColor="blue"
            onClick={() => onClickSave()}
            testSelector={`${testSelectorPrefix}-submit`}
          >
            Submit
          </ButtonWithLoading>
        </div>
      </div>
    </>
  );
};

async function createNewRequest(
  workflow: WorkflowDto | undefined,
  getValues: () => any,
) {
  const formData = getValues();
  const extraData = {
    ...formData.patient,
    organizationId: workflow?.organizationId,
    workflowId: workflow?.id,
  };

  return saveAndSubmitRequest({ ...formData, ...extraData })
    .catch((error: any) => console.error(error));
}

export { NewRequestSubmission as default };
